<template>
	<div class="row-actions">
		<div class="row-actions--header flex-1">
			<div class="selection">
				<span class="counter">{{ counterLabel }}</span>
				<span class="selection-label">{{ selectionLabel }} ({{ eventTagsDuration }})</span>
			</div>
			<div
				class="close flex-grow-0"
				@click="handleClose"
			>
				<Icon
					icon-name="e-remove"
					size="lg"
				/>
			</div>
		</div>
		<div class="actions flex-1">
			<a
				v-tooltip="actionDisabledTooltip"
				class="action sc-white"
				:class="{disabled: !canEdit }"
				data-qa="edit-selected-tags"
				@click="handleEdit"
			>
				<Icon
					icon-name="pen"
					size="lg"
				/>
				<span>{{ labels.editLabel }}</span>
			</a>
			<a
				v-tooltip="actionDisabledTooltip"
				class="action sc-white"
				:class="{disabled: !canEdit }"
				data-qa="move-selected-tags"
				@click="handleTimeshift"
			>
				<Icon
					icon-name="video-speed"
					size="lg"
				/>
				<span>{{ labels.moveLabel }}</span>
			</a>
			<a
				v-tooltip="actionDisabledTooltip"
				class="action sc-white"
				:class="{disabled: !canDelete }"
				data-qa="delete-selected-tags"
				@click="handleDelete"
			>
				<Icon
					icon-name="bin"
					size="lg"
				/>
				<span>{{ labels.deleteLabel }}</span>
			</a>
		</div>
	</div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { formatTimestampAsMinutesSeconds } from '@shared/utils/timeUtils';

export default {
	name:  'RowActions',
	props: {
		selected: {
			type:    Array,
			default: () => [],
		},
		total: {
			type:    Number,
			default: 0,
		},
	},
	computed: {
		...mapGetters({
			canEditEventTag: 'permissions/canEditEventTag',
		}),
		labels() {
			return {
				moveLabel:        this.transUcfirst('commons.move'),
				deleteLabel:      this.deleteBtnLabel,
				editLabel:        this.$I18n ? this.transUcfirst('commons.edit') : 'Edit',
				selectedLabel:    this.$I18n ? this.$I18n.trans('one.action_selected') : 'Tags selected',
				selectedAllLabel: this.$I18n ? this.$I18n.trans('one.action_selected_all') : 'All tags selected',
			};
		},
		counterLabel() {
			return this.selected.length === this.total
				? this.labels.selectedAllLabel.split(' ')[0]
				: this.selected.length;
		},
		selectionLabel() {
			// split the selected all label by ' '
			const parts = this.labels.selectedAllLabel.split(' ');
			// reconstruct the label from the second and third part of the split
			const selectedAllLabel = parts[1] + ' ' + parts[2];
			return this.selected.length === this.total
				? selectedAllLabel
				: this.labels.selectedLabel;
		},
		canEdit() {
			return this.selected.every(item => this.canEditEventTag(item.tag));
		},
		canDelete() {
			return this.canEdit;
		},
		actionDisabledTooltip() {
			return this.canEdit
				? null
				: { content: this.transUcfirst('one.permissions.view_only_message') };
		},
		eventTagsDuration() {
			const durationSeconds = this.selected.reduce((totalDuration, tag) => {
				return totalDuration + tag.durationSeconds;
			}, 0);

			return formatTimestampAsMinutesSeconds(durationSeconds);
		},
	},
	methods: {
		...mapActions({
			editTimeshift: 'eventTagEdit/editTimeshift',
			editEventTag:  'eventTagEdit/editEventTag',
		}),
		handleClose() {
			this.$emit('close');
		},
		handleEdit() {
			if (!this.canEdit) {
				return;
			}
			this.$emit('edit', this.selected);
		},
		handleTimeshift() {
			if (!this.canEdit) {
				return;
			}
			this.editTimeshift(this.selected);
		},
		handleDelete() {
			if (!this.canDelete) {
				return;
			}
			this.$emit('delete', this.selected);
		},
	},
};
</script>
<style lang="scss" scoped>
@import '@shared/sass/shared-variables';
	@import '@videotools/sass/_fonts';

	.row-actions {
		background: var(--color-grey-800);
		color: var(--color-soft-white);
		height: 148px;
		padding: 24px;

		&--header, .actions {
			display: flex;
		}

		&--header {
			justify-content: space-between;
			margin-bottom: 24px;
		}

		.actions {
			justify-content: space-evenly;
		}

		a.action {
			display: flex;
			flex-direction: column;
			align-items: center;
			height: 20px;

			@media (hover:hover) {
				&:hover {
					color: var(--color-grey-300);

					span {
						color: inherit;
						cursor: pointer;
					}
				}
			}

			&.disabled {
				color: var(--color-grey-400);

				span {
					color: inherit;
				}

				@media (hover:hover) {
					color: var(--color-grey-400);
					cursor: not-allowed;
					span {
						cursor: not-allowed;
					}
				}
			}
		}

		.actions span {
			@include text-sm-2;
			display: inline-block;
			padding-top: 10px;
			flex: 1 1 auto;
			text-align: center;
		}

		.close {
			cursor: pointer;
		}

		.selection {
			@include text-sm-2;

			.counter {
				display: inline-flex;
				justify-content: center;
				align-items: center;
				width: 34px;
				height: 24px;
				background: var(--color-hype-yellow);
				color: var(--color-shadow-grey);
				border-radius: 100px;
			}

			.selection-label {
				color: var(--color-soft-white);
				margin-left: 8px;
				padding: 0;
			}
		}
	}
</style>
